import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, FileText, Send, X, RotateCcw, Loader2, AlertTriangle,
    Receipt, ShieldCheck, Store as StoreIcon, CalendarDays,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
// `createAuthedAxios` is the DEFAULT EXPORT of hooks/useAxiosAuth.js — a plain
// factory that builds an axios instance, not a hook. Imported under its real
// name because eslint's react-hooks/rules-of-hooks keys on the identifier and
// would flag every call below as a misplaced hook. Same reasoning as
// hooks/usePermissions.js, which documents it at length.
import createAuthedAxios from '../../hooks/useAxiosAuth';

// The bills the pharmacy counter has already raised: find one, read it, print
// it, send it again.
//
// NOTHING ON THIS SCREEN CREATES OR EDITS A BILL, AND THAT IS DELIBERATE.
// A tax invoice number is allocated in exactly one place on the server, inside
// the transaction that moves the stock (services/pharmacy/billingService.js),
// because GST requires the numbers in a series to run consecutively within a
// financial year with no duplicates and no gaps. Everything here therefore acts
// on a document that already exists — which is why a cancellation is not on
// this page either: a cancelled bill is reversed by a credit note, never
// deleted, and raising one is a dispensing action rather than a filing one.

// ---------------------------------------------------------------- money ----

// DECIMAL columns come back from the API as STRINGS. node-postgres returns
// NUMERIC as text on purpose, so the money does not drift through a float on
// the way here — which means `invoice.cgstTotal + invoice.sgstTotal`
// CONCATENATES rather than adds. Every sum on this screen goes through paise
// and comes back out formatted, the same integer discipline the billing
// controller keeps on the other side of the wire. A screen that quietly used
// floats would eventually print a total that disagrees with the paper in the
// patient's hand, and the patient is the one who would find it.
const toPaise = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n)) return 0;
    // Half away from zero rather than Math.round, which breaks ties toward
    // +Infinity and would round -2.5 to -2. Credit notes and roundOff are
    // legitimately negative and must not be treated differently from their
    // positive twins.
    const scaled = n * 100;
    return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
};

const INR = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rupees = (value) => `₹${INR.format(toPaise(value) / 100)}`;
const rupeesFromPaise = (paise) => `₹${INR.format(paise / 100)}`;

// ----------------------------------------------------------------- time ----

// Rendered in the BROWSER's zone. The server cuts the day in the hospital's own
// zone (that is what the from/to filter means), so a laptop set to another
// country can show a bill under the neighbouring date. Acceptable here because
// the invoice number, not the printed timestamp, is what anybody reconciles
// against — but do not build a day-book total out of these strings.
// The time part is the one clock format ("2:30 PM", utils/clock).
const DATE_ONLY = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
});
const stampOf = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : `${DATE_ONLY.format(d)}, ${formatTime(d)}`;
};

// DATEONLY arrives as 'YYYY-MM-DD' and is NEVER passed through `new Date()`:
// that parses as UTC midnight, so an expiry of 2026-08-01 prints as 31 July for
// anybody west of Greenwich. On a dispensing record that is not a cosmetic
// slip — it is a batch that reads as a day more expired than it is.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateOnly = (value) => {
    if (!value) return '-';
    const [y, m, d] = String(value).slice(0, 10).split('-');
    if (!y || !m || !d) return String(value);
    return `${d} ${MONTHS[Number(m) - 1] || m} ${y}`;
};

const todayISO = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
const daysAgoISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// --------------------------------------------------------------- labels ----

const STATUS_STYLES = {
    paid: 'bg-green-50 text-green-700 border-green-200',
    partial: 'bg-blue-50 text-blue-700 border-blue-200',
    unpaid: 'bg-amber-50 text-amber-700 border-amber-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

const errorText = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

// Who the bill is for. No system id on screen (owner, 29 Sep 2026): a
// registered patient is named (or called "Patient") with the hospital's own
// patient code when the row carries one — never the patientId.
const partyOf = (inv, t) => {
    if (inv.walkInName) return inv.walkInName;
    if (!inv.patientId && !inv.patientName) return t('pharmacybills.walk_in', 'Walk-in');
    return [inv.patientName || t('common.patient_label', 'Patient'), hospitalPatientCode(inv)].filter(Boolean).join(' · ');
};

export default function PharmacyBills() {
    const { t } = useTranslation();
    const { can } = usePermissions();

    // Built once. The instance reads the access token in its request
    // interceptor, so a single memoised copy stays correct across a refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    // Resending puts a document carrying PHI in front of somebody, so it sits
    // behind its own permission server-side. Hiding the button is the polite
    // half only — api/routes/pharmacyBillingRoutes.js is the real gate.
    const canResend = can('pharmacy.bill.reprint');

    const [invoices, setInvoices] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [stores, setStores] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // A week, not "everything". The counter's usual question is about a bill
    // from today or the day before, and an unbounded default would make the
    // first paint of this page scan every invoice the hospital has ever issued.
    const [from, setFrom] = useState(() => daysAgoISO(6));
    const [to, setTo] = useState(() => todayISO());
    const [status, setStatus] = useState('');
    const [invoiceType, setInvoiceType] = useState('');
    const [storeId, setStoreId] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const [drawerInvoice, setDrawerInvoice] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [pdfBusyId, setPdfBusyId] = useState(null);
    const [resendTarget, setResendTarget] = useState(null);

    const searchTimeoutRef = useRef(null);

    // The server refuses an inverted range with a 400. Catching it here instead
    // means the user sees which field is wrong while they are still looking at
    // it, rather than a toast that says the request failed.
    const rangeInvalid = Boolean(from && to && to < from);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The counter picker. Loaded once — a hospital has a handful of licensed
    // premises, not a list worth paging.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/pharmacy/stores?status=active');
                if (!cancelled) setStores(res?.data?.data || []);
            } catch {
                // A missing picker narrows the list by nothing, which is the
                // same as the default. Not worth a toast on top of whatever the
                // invoice request is about to say.
            }
        })();
        return () => { cancelled = true; };
    }, [axiosInstance]);

    useEffect(() => {
        // Nothing to ask for, and the spinner must not be left running — the
        // banner below is the whole answer until one of the dates moves.
        if (rangeInvalid) { setListLoading(false); return undefined; }
        let cancelled = false;
        setListLoading(true);
        (async () => {
            try {
                const qs = new URLSearchParams();
                qs.append('page', String(currentPage));
                qs.append('limit', String(itemsPerPage));
                if (debouncedSearch) qs.append('search', debouncedSearch);
                if (status) qs.append('status', status);
                if (invoiceType) qs.append('invoiceType', invoiceType);
                if (storeId) qs.append('storeId', storeId);
                if (from) qs.append('from', from);
                if (to) qs.append('to', to);
                const res = await axiosInstance.get(`/pharmacy/invoices?${qs.toString()}`);
                if (cancelled) return;
                setInvoices(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (error) {
                if (cancelled) return;
                setInvoices([]);
                setTotal(0);
                toast.warn(errorText(error, t('pharmacybills.load_error', 'Failed to load the bills')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axiosInstance, currentPage, itemsPerPage, debouncedSearch, status, invoiceType, storeId, from, to, rangeInvalid, refreshKey]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const resetFilters = () => {
        setSearchQuery('');
        setStatus('');
        setInvoiceType('');
        setStoreId('');
        setFrom(daysAgoISO(6));
        setTo(todayISO());
        setCurrentPage(1);
    };

    const openDrawer = async (invoice) => {
        // The row is shown immediately and the lines fill in underneath. The
        // header figures are already on the list row, so there is nothing to
        // wait for before the drawer is useful.
        setDrawerInvoice(invoice);
        setDrawerLoading(true);
        try {
            const res = await axiosInstance.get(`/pharmacy/invoices/${invoice.invoiceId}`);
            const full = res?.data?.data;
            if (full) setDrawerInvoice(full);
        } catch (error) {
            toast.warn(errorText(error, t('pharmacybills.detail_error', 'Failed to load the bill')));
        } finally {
            setDrawerLoading(false);
        }
    };

    const closeDrawer = useCallback(() => setDrawerInvoice(null), []);

    useEffect(() => {
        if (!drawerInvoice) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerInvoice, closeDrawer]);

    /**
     * Open the bill PDF.
     *
     * THE TAB IS OPENED BEFORE THE REQUEST, not after it. The server renders the
     * document lazily on first ask and stores it, so this call can take a
     * second or two — and a window.open() that happens after an await has lost
     * its user gesture and is silently eaten by the pop-up blocker. Opening
     * first and pointing it at the URL afterwards is the only reliable order.
     */
    const handleViewPdf = async (invoice) => {
        setPdfBusyId(invoice.invoiceId);
        const win = window.open('', '_blank');
        // `opener` is severed by hand rather than through the 'noopener'
        // feature string, because that form makes window.open return null and
        // there would be nothing left to point at the URL.
        if (win) win.opener = null;

        try {
            const res = await axiosInstance.get(`/pharmacy/invoices/${invoice.invoiceId}/pdf`);
            const url = res?.data?.data?.url;
            if (!url) throw new Error(t('pharmacybills.pdf_missing', 'The bill PDF is not available'));
            if (win) {
                win.location.href = url;
            } else {
                // Pop-ups blocked. Say so rather than navigating this tab away
                // from the counter screen the pharmacist is working in.
                toast.info(t('pharmacybills.popup_blocked', 'Allow pop-ups for this site to open the bill PDF'));
            }
        } catch (error) {
            if (win) win.close();
            // 503 PDF_UNAVAILABLE means the BILL IS FINE and only the drawing
            // failed. Passing the server's wording through matters: a
            // pharmacist who reads this as "the sale did not happen" rings it
            // up a second time, which is a duplicate invoice number in the
            // statutory series and a credit note to undo it.
            toast.warn(errorText(error, t('pharmacybills.pdf_error', 'Failed to produce the bill PDF')));
        } finally {
            setPdfBusyId(null);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;


    return (
        <div data-testid="pharmacy-bills-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="pharmacy-bills-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('pharmacybills.title', 'Pharmacy bills')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('pharmacybills.subtitle', 'Every tax invoice the counter has raised. Bills are found and re-sent here — they are never re-issued, because a number belongs to one supply.')}
                    </p>
                </div>
                <button
                    data-testid="pharmacy-bills-refresh"
                    onClick={refetch}
                    disabled={listLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 whitespace-nowrap ${listLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    <RotateCcw className="w-4 h-4" />
                    {t('pharmacybills.refresh', 'Refresh')}
                </button>
            </div>

            {/* ------------------------------------------------- filters ---- */}
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
                <div className="relative lg:col-span-2">
                    <input
                        data-testid="bills-search-input"
                        type="text"
                        placeholder={t('pharmacybills.search_placeholder', 'Bill no / patient id / name…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <div className="relative">
                    <input
                        data-testid="bills-from-date"
                        type="date"
                        value={from}
                        max={to || undefined}
                        onChange={(e) => { setFrom(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-9 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>
                <div className="relative">
                    <input
                        data-testid="bills-to-date"
                        type="date"
                        value={to}
                        min={from || undefined}
                        onChange={(e) => { setTo(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-9 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="bills-status-filter"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('pharmacybills.all_statuses', 'All statuses')}</option>
                    <option value="unpaid">{t('pharmacybills.unpaid', 'Unpaid')}</option>
                    <option value="partial">{t('pharmacybills.partial', 'Part paid')}</option>
                    <option value="paid">{t('pharmacybills.paid', 'Paid')}</option>
                    <option value="cancelled">{t('pharmacybills.cancelled', 'Cancelled')}</option>
                </select>

                <select
                    data-testid="bills-type-filter"
                    value={invoiceType}
                    onChange={(e) => { setInvoiceType(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('pharmacybills.all_types', 'Bills & credit notes')}</option>
                    <option value="sale">{t('pharmacybills.type_sale', 'Bills only')}</option>
                    <option value="credit_note">{t('pharmacybills.type_credit', 'Credit notes only')}</option>
                </select>

                <div className="relative lg:col-span-2">
                    <select
                        data-testid="bills-store-filter"
                        value={storeId}
                        onChange={(e) => { setStoreId(e.target.value); setCurrentPage(1); }}
                        className="tt-select w-full p-2 pl-9 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    >
                        <option value="">{t('pharmacybills.all_stores', 'All counters')}</option>
                        {stores.map((s) => (
                            <option key={s.storeId} value={s.storeId}>{s.name}</option>
                        ))}
                    </select>
                    <StoreIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <button
                    data-testid="bills-reset-filters"
                    onClick={resetFilters}
                    className="w-full p-2 rounded-xl border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                    {t('pharmacybills.reset', 'Reset filters')}
                </button>
            </div>

            {rangeInvalid && (
                <div data-testid="bills-range-error" className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {t('pharmacybills.range_error', 'The “to” date is earlier than the “from” date, so there is nothing to show.')}
                </div>
            )}

            {/* ------------------------------------------------ the list ---- */}
            {listLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('pharmacybills.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="bills-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('pharmacybills.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('pharmacybills.bill', 'Bill')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('pharmacybills.party', 'For')}</th>
                                    <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('pharmacybills.taxable', 'Taxable')}</th>
                                    <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('pharmacybills.gst', 'GST')}</th>
                                    <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('pharmacybills.total', 'Total')}</th>
                                    <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('pharmacybills.settlement', 'Paid / Due')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('pharmacybills.status', 'Status')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('pharmacybills.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="bills-table-body">
                                {invoices.length > 0 ? invoices.map((inv, index) => {
                                    const taxP = toPaise(inv.cgstTotal) + toPaise(inv.sgstTotal) + toPaise(inv.igstTotal);
                                    const isCancelled = inv.status === 'cancelled';
                                    const isCreditNote = inv.invoiceType === 'credit_note';
                                    return (
                                        <tr
                                            key={inv.invoiceId}
                                            data-testid={`invoice-row-${inv.invoiceId}`}
                                            className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                        >
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs">
                                                <button
                                                    type="button"
                                                    data-testid={`open-invoice-${inv.invoiceId}`}
                                                    onClick={() => openDrawer(inv)}
                                                    className="font-semibold text-[#2D8A8A] hover:underline cursor-pointer font-mono"
                                                >
                                                    {inv.invoiceNo}
                                                </button>
                                                <div className="text-[11px] text-slate-400">{stampOf(inv.invoiceDate)}</div>
                                                {isCreditNote && (
                                                    <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200">
                                                        {t('pharmacybills.credit_note', 'Credit note')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{partyOf(inv, t)}</div>
                                                <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-[10px] font-semibold ${inv.patientType === 'IP' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {inv.patientType || 'OP'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-xs text-right text-gray-700">{rupees(inv.taxableTotal)}</td>
                                            <td className="p-3 text-xs text-right">
                                                <div className="font-semibold text-gray-700">{rupeesFromPaise(taxP)}</div>
                                                {/* An inpatient supply is a composite supply of healthcare
                                                    service and is GST-exempt, so a zero here is correct and
                                                    not a missing rate. Saying which of the two it is stops
                                                    somebody "fixing" it. */}
                                                {taxP === 0 && inv.patientType === 'IP' ? (
                                                    <div className="text-[10px] text-indigo-600">{t('pharmacybills.ip_exempt', 'exempt — inpatient')}</div>
                                                ) : (
                                                    <div className="text-[10px] text-slate-400">
                                                        {inv.isInterState
                                                            ? `IGST ${rupees(inv.igstTotal)}`
                                                            : `C ${rupees(inv.cgstTotal)} · S ${rupees(inv.sgstTotal)}`}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-right">
                                                <div className="font-bold text-gray-800">{rupees(inv.grandTotal)}</div>
                                                {toPaise(inv.roundOff) !== 0 && (
                                                    <div className="text-[10px] text-slate-400">
                                                        {t('pharmacybills.round_off', 'round off')} {rupees(inv.roundOff)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-right">
                                                <div className="text-green-700 font-semibold">{rupees(inv.paidTotal)}</div>
                                                {toPaise(inv.balance) > 0 && (
                                                    <div className="text-[11px] text-amber-600">{t('pharmacybills.due', 'due')} {rupees(inv.balance)}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${STATUS_STYLES[inv.status] || STATUS_STYLES.unpaid}`}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        data-testid={`view-invoice-${inv.invoiceId}`}
                                                        onClick={() => openDrawer(inv)}
                                                        title={t('pharmacybills.view', 'Open the bill')}
                                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md cursor-pointer transition-all duration-300 hover:scale-110"
                                                    >
                                                        <Receipt className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        data-testid={`view-pdf-${inv.invoiceId}`}
                                                        onClick={() => handleViewPdf(inv)}
                                                        disabled={pdfBusyId === inv.invoiceId}
                                                        title={t('pharmacybills.view_pdf', 'View PDF')}
                                                        className={`text-[#D7A05C] hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 hover:from-[#D7A05C] hover:to-[#b8863f] shadow-md transition-all duration-300 hover:scale-110 ${pdfBusyId === inv.invoiceId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        {pdfBusyId === inv.invoiceId
                                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                                            : <FileText className="h-4 w-4" />}
                                                    </button>
                                                    {canResend && (
                                                        <button
                                                            data-testid={`resend-invoice-${inv.invoiceId}`}
                                                            onClick={() => setResendTarget(inv)}
                                                            disabled={isCancelled}
                                                            title={isCancelled
                                                                ? t('pharmacybills.resend_cancelled', 'Cancelled by a credit note — send the credit note instead')
                                                                : t('pharmacybills.resend', 'Send this bill again')}
                                                            className={`text-[#2D8A8A] hover:text-white p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#2D8A8A] shadow-md transition-all duration-300 hover:scale-110 ${isCancelled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        >
                                                            <Send className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={9} className="p-6 text-center text-gray-500">
                                            {t('pharmacybills.no_bills', 'No bills in this range. Widen the dates or clear the filters.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg — the row carries nine columns and a
                        tablet at the counter is a real place this is read. */}
                    <div className="block lg:hidden space-y-4">
                        {invoices.length > 0 ? invoices.map((inv) => {
                            const taxP = toPaise(inv.cgstTotal) + toPaise(inv.sgstTotal) + toPaise(inv.igstTotal);
                            const isCancelled = inv.status === 'cancelled';
                            return (
                                <div key={inv.invoiceId} data-testid={`invoice-card-${inv.invoiceId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => openDrawer(inv)}
                                                className="font-mono font-semibold text-sm text-[#2D8A8A] hover:underline cursor-pointer"
                                            >
                                                {inv.invoiceNo}
                                            </button>
                                            <div className="text-[11px] text-slate-400">{stampOf(inv.invoiceDate)}</div>
                                            <div className="text-xs text-gray-700 mt-1">{partyOf(inv, t)} · {inv.patientType || 'OP'}</div>
                                        </div>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${STATUS_STYLES[inv.status] || STATUS_STYLES.unpaid}`}>
                                            {inv.status}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                                        <div>
                                            <div className="text-slate-400">{t('pharmacybills.taxable', 'Taxable')}</div>
                                            <div className="text-gray-700">{rupees(inv.taxableTotal)}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400">{t('pharmacybills.gst', 'GST')}</div>
                                            <div className="text-gray-700">{rupeesFromPaise(taxP)}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-400">{t('pharmacybills.total', 'Total')}</div>
                                            <div className="font-bold text-gray-800">{rupees(inv.grandTotal)}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <button onClick={() => openDrawer(inv)} className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs text-[#2D8A8A] font-semibold">
                                            {t('pharmacybills.view', 'Open')}
                                        </button>
                                        <button onClick={() => handleViewPdf(inv)} disabled={pdfBusyId === inv.invoiceId} className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs text-[#D7A05C] font-semibold">
                                            {t('pharmacybills.view_pdf', 'View PDF')}
                                        </button>
                                        {canResend && (
                                            <button onClick={() => setResendTarget(inv)} disabled={isCancelled} className={`flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs font-semibold ${isCancelled ? 'text-gray-400' : 'text-[#2D8A8A]'}`}>
                                                {t('pharmacybills.resend_short', 'Resend')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('pharmacybills.no_bills', 'No bills in this range. Widen the dates or clear the filters.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!listLoading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <InvoiceDrawer
                invoice={drawerInvoice}
                loading={drawerLoading}
                onClose={closeDrawer}
                onViewPdf={handleViewPdf}
                onResend={canResend ? setResendTarget : null}
                pdfBusy={pdfBusyId === drawerInvoice?.invoiceId}
                t={t}
            />

            <ResendDialog
                invoice={resendTarget}
                axiosInstance={axiosInstance}
                onClose={() => setResendTarget(null)}
                t={t}
            />
        </div>
    );
}

/* ==================================================================== *
 * The bill itself — lines, tax, and what was tendered against it.
 * ==================================================================== */

// The shell exists separately from the body so that AnimatePresence has a
// mounted parent to animate the exit from. Returning null from one component
// that owns both would unmount the whole subtree the moment the invoice clears,
// and the drawer would vanish instead of sliding away.
function InvoiceDrawer({ invoice, ...rest }) {
    return (
        <AnimatePresence>
            {invoice && <InvoiceDrawerBody invoice={invoice} {...rest} />}
        </AnimatePresence>
    );
}

function InvoiceDrawerBody({ invoice, loading, onClose, onViewPdf, onResend, pdfBusy, t }) {
    const items = invoice.items || [];
    const payments = invoice.payments || [];
    // The server recomputes the settlement from the tender rows rather than
    // trusting the cached paidTotal on the header, so prefer it when the detail
    // call has landed — the two can differ for a moment after a payment.
    const settlement = invoice.settlement || null;
    const balance = settlement ? settlement.balance : invoice.balance;
    const isCancelled = invoice.status === 'cancelled';
    const isIp = invoice.patientType === 'IP';

    return (
        <motion.div
            className="fixed inset-0 z-[9999] flex justify-end bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="invoice-drawer-backdrop"
        >
            <motion.aside
                role="dialog"
                aria-modal="true"
                aria-label={`Bill ${invoice.invoiceNo}`}
                data-testid="invoice-drawer"
                className="h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', bounce: 0.1, duration: 0.45 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white p-4 flex items-start justify-between">
                    <div>
                        <div className="font-mono text-lg font-bold" data-testid="drawer-invoice-no">{invoice.invoiceNo}</div>
                        <div className="text-[11px] opacity-90">
                            {stampOf(invoice.invoiceDate)}
                            {invoice.financialYear ? ` · FY ${invoice.financialYear}` : ''}
                            {invoice.isInterState ? ` · ${t('pharmacybills.inter_state', 'inter-state')}` : ''}
                            {invoice.placeOfSupply ? ` · ${invoice.placeOfSupply}` : ''}
                        </div>
                    </div>
                    <button
                        data-testid="close-invoice-drawer"
                        onClick={onClose}
                        className="p-2 rounded-xl hover:bg-white/20 cursor-pointer"
                        aria-label={t('pharmacybills.close', 'Close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <Field label={t('pharmacybills.party', 'For')} value={partyOf(invoice, t)} />
                        <Field label={t('pharmacybills.patient_type', 'Patient type')} value={invoice.patientType || 'OP'} />
                        <Field label={t('pharmacybills.type', 'Document')} value={invoice.invoiceType === 'credit_note' ? t('pharmacybills.credit_note', 'Credit note') : t('pharmacybills.type_sale_one', 'Tax invoice')} />
                        <Field label={t('pharmacybills.status', 'Status')} value={invoice.status} />
                    </div>

                    {isCancelled && (
                        <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-gray-400 mt-0.5" />
                            {/* A filed invoice cannot be edited or deleted, so this
                                row still stands and still appears in the return —
                                the credit note is what reverses it. */}
                            {t('pharmacybills.cancelled_note', 'This bill was cancelled by a credit note. The bill and its number both remain on file — that is what a cancellation looks like under GST.')}
                        </div>
                    )}

                    {isIp && (
                        <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] text-indigo-700">
                            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {t('pharmacybills.ip_note', 'Inpatient supply: medicines given as part of treatment are a composite supply of healthcare service and carry no GST. Zero tax on these lines is correct.')}
                        </div>
                    )}

                    {/* ------------------------------------- lines ---- */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-700 mb-2">{t('pharmacybills.items', 'Items')}</h3>
                        {loading && items.length === 0 ? (
                            <div className="flex items-center gap-2 text-xs text-slate-400 py-4">
                                <Loader2 className="w-4 h-4 animate-spin" /> {t('pharmacybills.loading', 'Loading...')}
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="invoice-items-table" className="w-full border-collapse text-[11px]">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600">
                                            <th className="p-2 text-left font-bold uppercase tracking-wider">{t('pharmacybills.item', 'Item')}</th>
                                            <th className="p-2 text-left font-bold uppercase tracking-wider">{t('pharmacybills.batch', 'Batch')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.qty', 'Qty')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.mrp', 'MRP')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.disc', 'Disc')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.taxable', 'Taxable')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.gst', 'GST')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('pharmacybills.line_total', 'Amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length > 0 ? items.map((it) => {
                                            const lineTaxP = toPaise(it.cgst) + toPaise(it.sgst) + toPaise(it.igst);
                                            return (
                                                <tr key={it.invoiceItemId} data-testid={`invoice-item-${it.invoiceItemId}`} className="border-b border-gray-100">
                                                    <td className="p-2">
                                                        <div className="font-semibold text-gray-700">{it.description}</div>
                                                        {it.hsnCode && <div className="text-slate-400">HSN {it.hsnCode}</div>}
                                                    </td>
                                                    <td className="p-2 text-gray-600">
                                                        <div className="font-mono">{it.batchNo || '-'}</div>
                                                        {/* The expiry as printed on the bill, formatted from
                                                            the plain date string — see dateOnly() above. */}
                                                        {it.expiryDate && <div className="text-slate-400">{t('pharmacybills.exp', 'exp')} {dateOnly(it.expiryDate)}</div>}
                                                    </td>
                                                    <td className="p-2 text-right text-gray-700">{it.qty}</td>
                                                    <td className="p-2 text-right text-gray-700">{rupees(it.unitPrice)}</td>
                                                    <td className="p-2 text-right text-gray-600">{rupees(it.discountAmount)}</td>
                                                    <td className="p-2 text-right text-gray-700">{rupees(it.taxableAmount)}</td>
                                                    <td className="p-2 text-right text-gray-700">
                                                        {rupeesFromPaise(lineTaxP)}
                                                        <div className="text-slate-400">{Number(it.gstRate)}%</div>
                                                    </td>
                                                    <td className="p-2 text-right font-semibold text-gray-800">{rupees(it.lineTotal)}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={8} className="p-4 text-center text-slate-400">
                                                    {t('pharmacybills.no_items', 'No lines on this bill')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ------------------------------------ totals ---- */}
                    <div className="rounded-xl border border-gray-200 p-3 text-xs space-y-1">
                        <TotalRow label={t('pharmacybills.sub_total', 'Sub total')} value={rupees(invoice.subTotal)} />
                        <TotalRow label={t('pharmacybills.discount', 'Discount')} value={rupees(invoice.discountTotal)} />
                        <TotalRow label={t('pharmacybills.taxable_total', 'Taxable value')} value={rupees(invoice.taxableTotal)} />
                        {invoice.isInterState ? (
                            <TotalRow label="IGST" value={rupees(invoice.igstTotal)} />
                        ) : (
                            <>
                                <TotalRow label="CGST" value={rupees(invoice.cgstTotal)} />
                                <TotalRow label="SGST" value={rupees(invoice.sgstTotal)} />
                            </>
                        )}
                        <TotalRow label={t('pharmacybills.round_off', 'Round off')} value={rupees(invoice.roundOff)} />
                        <div className="flex justify-between pt-2 mt-1 border-t border-gray-200 text-sm">
                            <span className="font-bold text-gray-700">{t('pharmacybills.grand_total', 'Grand total')}</span>
                            <span className="font-bold text-[#2D8A8A]" data-testid="drawer-grand-total">{rupees(invoice.grandTotal)}</span>
                        </div>
                    </div>

                    {/* ---------------------------------- payments ---- */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-700 mb-2">{t('pharmacybills.payments', 'Payments')}</h3>
                        {payments.length > 0 ? (
                            <div className="rounded-md border border-gray-200 divide-y divide-gray-100">
                                {payments.map((p) => (
                                    <div key={p.paymentId} data-testid={`invoice-payment-${p.paymentId}`} className="flex items-center justify-between p-2 text-xs">
                                        <div>
                                            <span className="font-semibold text-gray-700 capitalize">{p.method}</span>
                                            {/* A credit tender records that the patient was let go
                                                without paying. It is listed, but it never counts as
                                                money received — otherwise the balance would fall to
                                                zero and the bill would drop off the recovery screen. */}
                                            {p.method === 'credit' && (
                                                <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold">
                                                    {t('pharmacybills.on_credit', 'on credit — not collected')}
                                                </span>
                                            )}
                                            {p.status !== 'received' && (
                                                <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-semibold capitalize">{p.status}</span>
                                            )}
                                            <div className="text-[11px] text-slate-400">
                                                {stampOf(p.receivedAt)}{p.reference ? ` · ${p.reference}` : ''}
                                            </div>
                                        </div>
                                        <div className="font-semibold text-gray-800">{rupees(p.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-slate-400 py-2">
                                {loading ? t('pharmacybills.loading', 'Loading...') : t('pharmacybills.no_payments', 'Nothing tendered against this bill yet')}
                            </div>
                        )}
                        <div className="flex justify-between mt-2 text-xs">
                            <span className="text-slate-500">{t('pharmacybills.balance', 'Balance due')}</span>
                            <span className={`font-bold ${toPaise(balance) > 0 ? 'text-amber-600' : 'text-green-700'}`} data-testid="drawer-balance">
                                {rupees(balance)}
                            </span>
                        </div>
                        {settlement && toPaise(settlement.creditTotal) > 0 && (
                            <div className="flex justify-between mt-1 text-[11px] text-slate-400">
                                <span>{t('pharmacybills.promised', 'Booked to the patient’s account')}</span>
                                <span>{rupees(settlement.creditTotal)}</span>
                            </div>
                        )}
                    </div>

                    {/* ----------------------------------- actions ---- */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button
                            data-testid="drawer-view-pdf"
                            onClick={() => onViewPdf(invoice)}
                            disabled={pdfBusy}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#D7A05C] text-[#b8863f] font-semibold text-sm hover:bg-[#D7A05C]/10 ${pdfBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            {t('pharmacybills.view_pdf', 'View PDF')}
                        </button>
                        {onResend && (
                            <button
                                data-testid="drawer-resend"
                                onClick={() => onResend(invoice)}
                                disabled={isCancelled}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-lg hover:shadow-xl ${isCancelled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <Send className="w-4 h-4" />
                                {t('pharmacybills.resend', 'Send this bill again')}
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        {t('pharmacybills.resend_footnote', 'Sending a copy re-delivers this bill. It keeps the number it already has — nothing here issues a new one.')}
                    </p>
                </div>
            </motion.aside>
        </motion.div>
    );
}

const Field = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-gray-700 font-semibold capitalize">{value || '-'}</div>
    </div>
);

const TotalRow = ({ label, value }) => (
    <div className="flex justify-between text-gray-600">
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

/* ==================================================================== *
 * Resend — the one action on this page that leaves the building.
 * ==================================================================== */

const CHANNELS = ['email', 'sms', 'whatsapp'];

function ResendDialog({ invoice, axiosInstance, onClose, t }) {
    const [channel, setChannel] = useState('email');
    const [recipient, setRecipient] = useState('');
    const [sending, setSending] = useState(false);
    const [needsRecipient, setNeedsRecipient] = useState(false);

    useEffect(() => {
        if (invoice) {
            setChannel('email');
            setRecipient('');
            setNeedsRecipient(false);
        }
    }, [invoice]);

    if (!invoice) return null;

    const submit = async () => {
        setSending(true);
        try {
            const body = { channel };
            if (recipient.trim()) body.recipient = recipient.trim();
            const res = await axiosInstance.post(`/pharmacy/invoices/${invoice.invoiceId}/resend`, body);
            const data = res?.data?.data || {};
            // The confirmation names the invoice number back on purpose: the
            // person asked to "send the bill again" and the useful reassurance
            // is that it is the SAME bill. The number and the address are
            // appended rather than interpolated into the translation default,
            // because a translated string would drop values a default carries.
            const sentTo = [data.invoiceNo || invoice.invoiceNo, data.recipient].filter(Boolean).join(' → ');
            toast.success(`${t('pharmacybills.resend_queued', 'A copy has been queued for delivery')}: ${sentTo}`);
            if (data.pdfAttached === false) {
                // The message still goes, but without the bill in it. Worth its
                // own line: the patient will ask where the attachment is.
                toast.info(t('pharmacybills.resend_nopdf', 'The PDF could not be attached to this copy'));
            }
            onClose();
        } catch (error) {
            // The server answers NO_RECIPIENT when nothing is on file. That is
            // not a failure to explain away — it is a field this dialog now has
            // to ask for, so keep the dialog open and point at it.
            if (error?.response?.data?.code === 'NO_RECIPIENT') {
                setNeedsRecipient(true);
            }
            toast.warn(errorText(error, t('pharmacybills.resend_error', 'Failed to send the bill')));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="resend-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('pharmacybills.resend_title', 'Send this bill again')}</h2>
                        <p className="text-[11px] text-slate-400">{t('pharmacybills.resend_sub', 'A copy goes out on the channel you pick.')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 cursor-pointer" aria-label={t('pharmacybills.close', 'Close')}>
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* THE POINT OF THE WHOLE DIALOG, said in the user's language.
                    GST requires the numbers in a series to be consecutive within
                    a financial year, so re-issuing would file one handover of
                    medicine as two supplies and the only way back is a credit
                    note. Somebody who does not know that will keep asking for
                    "a fresh bill" until the screen tells them why it is the
                    same one. */}
                <div className="rounded-xl border border-[#4ABDBD]/40 bg-[#4ABDBD]/5 p-3 mb-4" data-testid="resend-number-notice">
                    <div className="flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#2D8A8A] flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] leading-relaxed text-[#2D8A8A]">
                            {t('pharmacybills.resend_notice_1', 'The bill keeps its number —')}{' '}
                            <span className="font-mono font-bold">{invoice.invoiceNo}</span>.{' '}
                            {t('pharmacybills.resend_notice_2', 'This re-delivers the document; it does not re-issue it. A second number would file one sale twice in the GST return, and undoing that needs a credit note.')}
                        </p>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('pharmacybills.channel', 'Send by')}</label>
                    <div className="flex gap-2">
                        {CHANNELS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`resend-channel-${c}`}
                                onClick={() => setChannel(c)}
                                className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize border transition-all cursor-pointer ${channel === c
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white border-transparent shadow'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-[#4ABDBD]'}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {channel === 'email'
                            ? t('pharmacybills.recipient_email', 'Email address')
                            : t('pharmacybills.recipient_phone', 'Phone number')}
                        <span className="font-normal text-slate-400"> — {t('pharmacybills.optional', 'optional')}</span>
                    </label>
                    <input
                        data-testid="resend-recipient"
                        type={channel === 'email' ? 'email' : 'tel'}
                        value={recipient}
                        onChange={(e) => { setRecipient(e.target.value); setNeedsRecipient(false); }}
                        placeholder={t('pharmacybills.recipient_placeholder', 'Leave blank to use what is on file')}
                        className={`w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${needsRecipient ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {needsRecipient && (
                        <p className="text-[11px] text-red-600 mt-1">
                            {t('pharmacybills.no_recipient', 'Nothing is on file for this bill — type where it should go.')}
                        </p>
                    )}
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('pharmacybills.cancel', 'Cancel')}
                    </button>
                    <button
                        data-testid="resend-submit"
                        onClick={submit}
                        disabled={sending}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-lg hover:shadow-xl ${sending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {t('pharmacybills.send_copy', 'Send a copy')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
